import {
  calculateCombinedMultiInvestment,
  calculateMultiProjectedPay,
  calculateMultiReturn,
} from '@mobi/betslip/helpers/calculator/multis'
import {
  isFobDetails,
  isFobSelection,
  isRaceDetails,
  isToteSelection,
} from '@mobi/betslip/helpers/typeGuards'
import { calculateEstReturnSingleItem } from '@mobi/betslip/helpers/calculator/singles'
import { selectMultiInvestment } from '@mobi/betslip/Store/Bets/selectors'
import { formatKambiCurrencyToDollarDecimal } from '@mobi/sports-offering/helpers/currency/currencyFormat'
import { store } from '@core/Store'
import { trackShareBetShared } from '@core/Areas/ShareBet/helpers/analytics'
import type { ShareBetData } from '@core/Areas/ShareBet/types'
import type { BetSlipItem } from '@mobi/betslip/types'
import type { Ticket, TicketLeg } from '@core/Areas/AccountActivity/types'
import type { BetLoopShareTicket } from '@core/Areas/BetLoop/types'
import type { RaceCodesLowerCase } from '@core/Areas/Racing/Types/MeetingInformation'
import { meetingIdToContestType } from '@core/Areas/Racing/helpers/meetingIdToContestType'
import { isKambiSharedCoupon } from '@core/Areas/BetLoop/helpers/typeGuards/isKambiSharedCoupon'
import { getKambiCouponBetStatus } from '@core/Areas/KambiSports/Player/helpers/getKambiCouponBetStatus'
import { getKambiCouponDetailsForTracking } from '@core/Areas/KambiSports/Player/helpers/analytics'
import {
  isBetLoopShareData,
  isBetSlipShareData,
  isKambiSportsShareData,
  isQuickbetShareData,
  isTicketShareData,
} from '../../helpers/typeGuards'

export function trackShareBetClick({
  data,
  isCostShared,
  destination,
  sourceLocation,
}: {
  data: ShareBetData
  isCostShared: boolean
  destination?: Parameters<typeof trackShareBetShared>[0]['destination']
  sourceLocation?: string
}) {
  if (isTicketShareData(data)) {
    trackShareBetShared({
      location: data.share.sourceLocation ?? sourceLocation ?? 'transacted-bets',
      betType: data.share.ticket.productName,
      spend: data.share.ticket.investment.total,
      payout: data.share.ticket.return,
      betStatus: data.share.ticket.status,
      costShared: isCostShared,
      odds: data.share.ticket.combinedDividend,
      destination,
      raceCode: getRaceCodeFromTicket(data.share.ticket),
    })
    return
  }

  if (isBetLoopShareData(data)) {
    if (isKambiSharedCoupon(data.share.ticket)) {
      const coupon = data.share.ticket
      const { betType, sport, league, match } = getKambiCouponDetailsForTracking(coupon)
      trackShareBetShared({
        location: sourceLocation ?? 'bet-loop',
        betType: betType,
        spend: null,
        payout: formatKambiCurrencyToDollarDecimal(coupon.bets[0].potentialPayout),
        betStatus: getKambiCouponBetStatus(coupon.bets[0]?.betStatus) ?? 'Pending',
        costShared: false,
        odds: coupon.bets[0].betOdds / 1000,
        destination: 'External',
        sportCode: sport,
        league: league,
        match: match,
      })
      return
    } else {
      trackShareBetShared({
        location: sourceLocation ?? 'bet-loop',
        betType: data.share.ticket.productName,
        spend: null,
        payout: data.share.ticket.return,
        betStatus: data.share.ticket.status,
        costShared: false,
        odds: data.share.ticket.combinedDividend,
        destination: 'External',
        raceCode: getRaceCodeFromTicket(data.share.ticket),
      })
    }

    return
  }

  if (isKambiSportsShareData(data)) {
    const coupon = data.share.coupon
    const bet = coupon.bets[0]
    const url = new URL(window.location.href)
    const location = url.pathname.includes('bet-loop') ? 'bet-loop-quickbet' : 'quickbet'
    const { betType, sport, league, match } = getKambiCouponDetailsForTracking(coupon)

    trackShareBetShared({
      location: sourceLocation ?? location,
      betType: betType,
      spend: formatKambiCurrencyToDollarDecimal(bet.stake),
      payout: formatKambiCurrencyToDollarDecimal(bet.potentialPayout),
      betStatus: getKambiCouponBetStatus(bet?.betStatus) ?? 'Pending',
      costShared: isCostShared,
      odds: bet.betOdds / 1000,
      destination,
      sportCode: sport,
      league: league,
      match: match,
    })
    return
  }

  let spend: number | undefined
  let payout: number | undefined
  let odds: number | null | undefined

  const isMultiBet = data.share.items.length > 1
  const items = data.share.items

  const isBetPlaced =
    (isBetSlipShareData(data) && data.share.isBetPlaced) || isQuickbetShareData(data)

  if (isBetPlaced) {
    if (isMultiBet) {
      const multiInvestment = selectMultiInvestment(store.getState())
      spend = calculateCombinedMultiInvestment(items, multiInvestment)
      payout = calculateMultiProjectedPay(items, multiInvestment)
      odds = calculateMultiReturn(items)
    } else {
      const winInvestment = items[0].investment.win.value
      const placeInvestment = items[0].investment.place.value
      spend = winInvestment + placeInvestment
      payout = calculateEstReturnSingleItem(items[0])
      odds = isFobSelection(items[0].selection)
        ? winInvestment
          ? items[0].selection.winPrice
          : items[0].selection.placePrice
        : undefined
    }
  }

  if (isBetSlipShareData(data)) {
    trackShareBetShared({
      location: 'bet-slip',
      betType: isMultiBet ? 'multi' : 'single',
      spend,
      payout,
      odds,
      betStatus: data.share.isBetPlaced ? 'Pending' : 'Unplaced',
      costShared: isCostShared,
      destination,
      raceCode: getRaceCodeFromBetSlipItems(data.share.items),
    })
    return
  }

  if (isQuickbetShareData(data)) {
    const selection = data.share.items[0].selection
    const url = new URL(window.location.href)
    const location = url.pathname.includes('bet-loop') ? 'bet-loop-quickbet' : 'quickbet'
    let betType
    if ('betType' in selection) {
      betType = selection.betType
    } else if ('type' in selection) {
      betType = selection.type
    }

    trackShareBetShared({
      location: location,
      betType,
      spend: data.share.betCost ? data.share.betCost : spend,
      payout,
      odds: isToteSelection(items[0].selection) ? null : odds,
      betStatus: 'Pending',
      costShared: isCostShared,
      destination,
      raceCode: getRaceCodeFromBetSlipItems(data.share.items),
    })
  }
}

export function getRaceCodeFromTicket(
  ticket: Ticket | BetLoopShareTicket
): ShortRaceCode | undefined {
  const legs = ticket.legs
  if (!legs || legs.length === 0) return undefined

  const firstLegRaceCode = getRaceCodeFromTicketLeg(legs[0])
  const isMixed = legs.some(leg => getRaceCodeFromTicketLeg(leg) !== firstLegRaceCode)

  if (isMixed) return 'combination'
  return firstLegRaceCode || undefined
}

// =============
// Local Helpers
// =============

function getRaceCodeFromBetSlipItems(items: BetSlipItem[]): ShortRaceCode | undefined {
  if (!items || items.length === 0) return undefined

  const raceTypes = items.flatMap(({ selectionDetails }) => {
    if (isRaceDetails(selectionDetails)) {
      const raceCode = selectionDetails.races[0].meetingCode.toLowerCase() as RaceCodesLowerCase
      return [raceCode]
    }
    if (isFobDetails(selectionDetails)) {
      return [selectionDetails.sportIconCode as RaceCodesLowerCase]
    }
    return []
  })
  if (raceTypes.length === 0) return undefined

  const firstType = raceTypes[0]
  const isMixed = raceTypes.some(type => type !== firstType)

  if (isMixed) return 'combination'
  return firstType || undefined
}

const mapEventTypeToRaceCodes: Record<string, ShortRaceCode> = {
  greyhounds: 'dogs',
  racing: 'races',
  trots: 'trots',
}

function getRaceCodeFromTicketLeg(leg: TicketLeg): ShortRaceCode | undefined {
  if (leg.eventType) return mapEventTypeToRaceCodes[leg.eventType?.toLowerCase()] || undefined
  return meetingIdToContestType(leg.fixtureId)
}

// =====
// Types
// =====

type ShortRaceCode = RaceCodesLowerCase | 'combination'
